import { Align, Block, escapeText, Inline, linkMarkup, normalizeUrl, Style, styledMarkup } from './paragraphFormat'

/*
 * The paragraph editor's DOM ↔ the stored markup (paragraphFormat.ts). The
 * editor lets the browser edit (execCommand), so the DOM holds whatever the
 * browser made — <b>/<strong>, <i>, <strike>, <font color>, styled spans,
 * <ul><li>, <div> lines (aligned by their text-align), <br>s and "\n"s —
 * and serialize() reads all of it back.
 *
 * Every line is its own <div>: that is what the browser's Enter and
 * justify commands work with, and a new line keeps the alignment.
 */

/** the accent — the dashboard's brand blue, the pop-up title's colour */
export const ACCENT = '#2d89f4'

export const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
const TAGS: Record<Style, [string, string]> = { b: ['<b>', '</b>'], i: ['<i>', '</i>'], s: ['<strike>', '</strike>'], a: [`<font color="${ACCENT}">`, '</font>'] }

export const codeMarkupHtml = (code: string, cls: string) => `<span class="${cls}" contenteditable="false" data-code="${esc(code)}">${esc(code)}</span>`

function inlineHtml(nodes: Inline[], cls: string): string {
  return nodes.map((n) => {
    if (n.t === 'text') return esc(n.v)
    if (n.t === 'link') return `<a href="${esc(n.href)}">${esc(n.v)}</a>`
    if (n.t === 'code') return codeMarkupHtml(n.v, cls)
    return TAGS[n.t][0] + inlineHtml(n.c, cls) + TAGS[n.t][1]
  }).join('')
}

/** the stored markup as the editor's HTML; `cls` is the promo code chip's class */
export function toHtml(blocks: Block[], cls: string): string {
  return blocks.map((b) => {
    if (b.t === 'list') return `<ul>${b.items.map((it) => `<li>${inlineHtml(it, cls) || '<br>'}</li>`).join('')}</ul>`
    return `<div${b.align ? ` style="text-align: ${b.align};"` : ''}>${inlineHtml(b.c, cls) || '<br>'}</div>`
  }).join('')
}

/* ── the DOM back into markup ───────────────────────────────────────── */

type Token =
  | { k: 'text'; v: string; st: Set<Style> }
  | { k: 'link'; v: string; href: string; st: Set<Style> }
  | { k: 'code'; v: string }
  | { k: 'nl' }
  | { k: 'bullet' }
  | { k: 'align'; v: Align }

const sameStyles = (a: Set<Style>, b: Set<Style>) => a.size === b.size && Array.from(a).every((x) => b.has(x))

export function colorIsAccent(color: string): boolean {
  const c = color.trim().toLowerCase().replace(/\s/g, '')
  return c === ACCENT || c === 'rgb(45,137,244)'
}

function stylesOf(el: HTMLElement, parent: Set<Style>): Set<Style> {
  const st = new Set(parent)
  const tag = el.tagName
  if (tag === 'B' || tag === 'STRONG') st.add('b')
  if (tag === 'I' || tag === 'EM') st.add('i')
  if (tag === 'S' || tag === 'STRIKE' || tag === 'DEL') st.add('s')
  if (tag === 'FONT' && el.getAttribute('color')) {
    if (colorIsAccent(el.getAttribute('color') || '')) st.add('a'); else st.delete('a')
  }
  const css = el.style
  if (css.fontWeight) { if (css.fontWeight === 'bold' || Number(css.fontWeight) >= 600) st.add('b'); else st.delete('b') }
  if (css.fontStyle) { if (css.fontStyle === 'italic') st.add('i'); else st.delete('i') }
  if (css.textDecoration || css.textDecorationLine) {
    if (`${css.textDecoration} ${css.textDecorationLine}`.includes('line-through')) st.add('s')
  }
  if (css.color) { if (colorIsAccent(css.color)) st.add('a'); else st.delete('a') }
  return st
}

/** a block's own alignment: center / right, left (resets), or none (inherits) */
function alignOf(el: HTMLElement): Align | 'left' | undefined {
  const v = (el.style.textAlign || el.getAttribute('align') || '').toLowerCase()
  if (v === 'center' || v === 'right') return v
  if (v === 'left' || v === 'start' || v === 'justify') return 'left'
  return undefined
}

function tokenize(root: HTMLElement): Token[] {
  const out: Token[] = []
  const breakLine = () => { if (out.length && out[out.length - 1].k !== 'nl') out.push({ k: 'nl' }) }
  const walk = (node: Node, st: Set<Style>, inItem: boolean, align?: Align) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) { out.push({ k: 'text', v: (child.textContent || '').replace(/\u00a0/g, ' '), st }); return }
      if (!(child instanceof HTMLElement)) return
      const tag = child.tagName
      if (tag === 'BR') {
        // the last <br> of a block is the browser's placeholder; inside a list item a line break is a space
        if (!child.nextSibling && node !== root) return
        if (inItem) out.push({ k: 'text', v: ' ', st })
        else { out.push({ k: 'nl' }); if (align) out.push({ k: 'align', v: align }) }
        return
      }
      if (child.dataset.code) { out.push({ k: 'code', v: child.dataset.code }); return }
      if (tag === 'A') {
        const href = normalizeUrl(child.getAttribute('href') || '')
        const text = (child.textContent || '').replace(/\u00a0/g, ' ')
        out.push(href && text.trim() ? { k: 'link', v: text, href, st } : { k: 'text', v: text, st })
        return
      }
      if (tag === 'UL' || tag === 'OL') {
        breakLine()
        child.querySelectorAll(':scope > li').forEach((li) => {
          out.push({ k: 'bullet' })
          walk(li, st, true)
          out.push({ k: 'nl' })
        })
        return
      }
      if (!inItem && (tag === 'DIV' || tag === 'P')) {
        // a line of its own; an empty one is a blank line
        breakLine()
        const own = alignOf(child)
        const lineAlign = own === 'left' ? undefined : own || align
        const from = out.length
        if (lineAlign) out.push({ k: 'align', v: lineAlign })
        walk(child, stylesOf(child, st), false, lineAlign)
        const empty = out.slice(from).every((t) => t.k === 'align')
        if (empty) { out.length = from; out.push({ k: 'nl' }) } else breakLine()
        return
      }
      walk(child, stylesOf(child, st), inItem, align)
    })
  }
  walk(root, new Set(), false)
  return out
}

export function serialize(root: HTMLElement): string {
  const tokens = tokenize(root)
  let out = ''
  let lineStart = true
  let align: Align | undefined
  for (let i = 0; i < tokens.length; i += 1) {
    const tk = tokens[i]
    if (tk.k === 'nl') { out += '\n'; lineStart = true; align = undefined; continue }
    if (tk.k === 'align') { if (lineStart) { out += `{${tk.v}}`; align = tk.v } continue }
    if (tk.k === 'bullet') { out += '- '; lineStart = false; continue }
    if (tk.k === 'code') { out += `{code:${tk.v}}`; lineStart = false; continue }
    if (tk.k === 'link') { out += styledMarkup(linkMarkup(tk.v, tk.href), tk.st); lineStart = false; continue }
    // neighbouring text in the same styles is one run
    let text = tk.v
    while (i + 1 < tokens.length) {
      const next = tokens[i + 1]
      if (next.k !== 'text' || !sameStyles(next.st, tk.st)) break
      text += next.v
      i += 1
    }
    if (!text) continue
    // the browser may keep a line break as a "\n" in the text: markers never span one, the alignment goes on
    const atStart = lineStart
    const lineAlign = align
    out += text.split('\n').map((line, k) => {
      let escaped = escapeText(line)
      // a typed "- " at the start of a line is text, not a list
      if ((k > 0 || atStart) && escaped.startsWith('- ')) escaped = `\\${escaped}`
      return (k > 0 && lineAlign ? `{${lineAlign}}` : '') + styledMarkup(escaped, tk.st)
    }).join('\n')
    lineStart = text.endsWith('\n')
  }
  out = out.replace(/[ \t]+\n/g, '\n').replace(/\{(center|right)\}(?=\n|$)/g, '').replace(/\n+$/, '')
  return out.trim() ? out : ''
}

/* ── lines, for alignment ───────────────────────────────────────────
   The browser's justify commands treat a promo code chip (not editable)
   as the end of a block: they split the line around it, and the code falls
   out of the aligned line. So alignment is set here, on the whole line. */

const isBlock = (n: Node | null): boolean => !!n && n instanceof HTMLElement && ['DIV', 'P', 'UL', 'OL'].includes(n.tagName)
const isBreak = (n: Node | null): boolean => !n || isBlock(n) || (n instanceof HTMLElement && n.tagName === 'BR')

/** the top-level line holding `node`; a loose run of text at the root (no <div> yet) is wrapped into one */
export function lineOf(root: HTMLElement, node: Node): HTMLElement | null {
  let n: Node | null = node
  if (n === root) return null
  while (n && n.parentNode !== root) n = n.parentNode
  if (!n) return null
  if (n instanceof HTMLElement && (n.tagName === 'UL' || n.tagName === 'OL')) return null
  if (n instanceof HTMLElement && (n.tagName === 'DIV' || n.tagName === 'P')) return n
  if (n instanceof HTMLElement && n.tagName === 'BR') {
    // an empty line at the root
    const div = document.createElement('div')
    root.replaceChild(div, n)
    div.appendChild(document.createElement('br'))
    return div
  }
  let first: Node = n
  while (!isBreak(first.previousSibling)) first = first.previousSibling as Node
  let last: Node = n
  while (!isBreak(last.nextSibling)) last = last.nextSibling as Node
  const div = document.createElement('div')
  root.insertBefore(div, first)
  let cur: Node | null = first
  while (cur) {
    const next: Node | null = cur.nextSibling
    div.appendChild(cur)
    if (cur === last) break
    cur = next
  }
  // the <br> that ended the run ends the line now
  const after = div.nextSibling
  if (after instanceof HTMLElement && after.tagName === 'BR') after.remove()
  return div
}

/** the lines a selection touches, first to last */
export function linesInRange(root: HTMLElement, range: Range): HTMLElement[] {
  const at = (container: Node, offset: number, end: boolean) =>
    container === root ? root.childNodes[end ? Math.max(0, offset - 1) : offset] || root.lastChild : container
  const startNode = at(range.startContainer, range.startOffset, false)
  const endNode = at(range.endContainer, range.endOffset, true)
  const first = startNode ? lineOf(root, startNode) : null
  const last = endNode ? lineOf(root, endNode) : null
  if (!first) return last ? [last] : []
  if (!last || first === last) return [first]
  const out: HTMLElement[] = []
  let cur: Element | null = first
  while (cur) {
    if (cur.tagName === 'DIV' || cur.tagName === 'P') out.push(cur as HTMLElement)
    if (cur === last) break
    cur = cur.nextElementSibling
  }
  return out
}

export function setLineAlign(lines: HTMLElement[], align: 'left' | 'center' | 'right') {
  lines.forEach((line) => {
    if (align === 'left') line.style.removeProperty('text-align')
    else line.style.textAlign = align
    if (!line.getAttribute('style')) line.removeAttribute('style')
  })
}

/** put nodes where the range is (or at the end of the last line) and the caret after them */
export function insertAtRange(root: HTMLElement, range: Range | null, nodes: Node[]) {
  let r = range
  if (!r) {
    r = document.createRange()
    const lastLine = root.lastElementChild && (root.lastElementChild.tagName === 'DIV' || root.lastElementChild.tagName === 'P') ? root.lastElementChild : root
    r.selectNodeContents(lastLine)
    r.collapse(false)
  }
  r.deleteContents()
  const frag = document.createDocumentFragment()
  nodes.forEach((n) => frag.appendChild(n))
  r.insertNode(frag)
  const caret = document.createRange()
  caret.setStartAfter(nodes[nodes.length - 1])
  caret.collapse(true)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(caret)
}
